import { Component, OnInit } from "@angular/core";
import { AppService } from "@services/app.service";
import { Subject, takeUntil } from "rxjs";
import { DatePipe } from "@angular/common";
import { NgxSpinnerService } from "ngx-spinner";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { printDataService } from "@/model/new-joining-form/printDataService";
import { NewJoinForms } from "@/model/new-joining-form/new_JoinForms";


@Component({
  selector: "app-new-joining-attendance-report",
  templateUrl: "./new-joining-attendance-report.component.html",
  styleUrls: ["./new-joining-attendance-report.component.scss"]
})
export class NewJoiningAttendanceReportComponent implements OnInit {
  searchText: any;
  destroy$: Subject<boolean> = new Subject<boolean>();
  datePipe: DatePipe = new DatePipe("en-IN");
  getAllEmployees: NewJoinForms[] = [];
  currentDate: string | null = null;
  disablenav = true;
  fromDate: string;
  toDate: string;
  selectedMonth: string;
  employeeData: any[] = [];
  savedRecords: Set<number> = new Set();
  workingDaysMap: { [key: number]: number } = {};
  leaveDaysMap: { [key: number]: number } = {};
  currentDetails: any;
  totalDays: number = 0;
  userType: string = "";
  constructor(private appService: AppService, private tostar: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router) {
    this.currentDate = this.datePipe.transform(new Date(), "yyyy-MM-dd") || null;
  }

  ngOnInit() {
    this.userType = localStorage.getItem('userType');
  }

  onDateChange(): void {
    if (!this.fromDate || !this.toDate) {
      this.selectedMonth = null;
      this.totalDays = 0;
      return;
    }
  
    const fromDateObj = new Date(this.fromDate);
    const toDateObj = new Date(this.toDate);
  
    this.selectedMonth = `${fromDateObj.toLocaleString('en-US', { month: 'long' })}-${fromDateObj.getFullYear()}`;
    this.totalDays = Math.floor((toDateObj.getTime() - fromDateObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  
    if (this.totalDays < 0) {
      this.tostar.error("Invalid date range. Please select valid From Date and To Date.");
      this.totalDays = 0;
      return;
    }
  
    this.checkAlreadyAttendance();
  }
  
  getEmployeeDetails() {
    this.spinner.show();
    this.appService.getCurrentEmployeeDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.spinner.hide();
        this.employeeData = data;
  
        // Initialize workingDays & leaveDays for each employee
        this.employeeData.forEach((_, i) => {
          this.leaveDaysMap[i] = 0;
          this.workingDaysMap[i] = this.totalDays;
        });
      }, (err: any) => {
        this.spinner.hide();
        this.tostar.error(err.message || "Something Went Wrong, Please Try Again");
      });
  }
  
  checkAlreadyAttendance() {
    this.spinner.show();
    this.appService.checkAlreadyAttendanceDetails(this.selectedMonth)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.spinner.hide();
        if (data.code === 200) {
          this.getEmployeeDetails();
        }
      }, (error: any) => {
        this.spinner.hide();
        if (error.status === 409) {
          this.tostar.error("Attendance for the selected month already exists");
        } else {
          this.tostar.error(error.message || "Something Went Wrong, Please Try Again.");
        }
      });
  }
  
  updateWorkingDays(index: number): void {
    const leaveDays = this.leaveDaysMap[index] || 0;
  
    if (leaveDays < 0) {
      this.tostar.error("Leave days cannot be negative.");
      this.leaveDaysMap[index] = 0;
    } else if (leaveDays > this.totalDays) {
      this.tostar.error(`Leave days cannot exceed total days (${this.totalDays}).`);
      this.leaveDaysMap[index] = this.totalDays;
    }
  
    // Update working days
    this.workingDaysMap[index] = this.totalDays - this.leaveDaysMap[index];
  }
  
  validateDays(index: number): boolean {
    const workingDays = this.workingDaysMap[index] || 0;
    const leaveDays = this.leaveDaysMap[index] || 0;
  
    if (workingDays < 0 || leaveDays < 0) {
      this.tostar.error("Days cannot be negative.");
      return false;
    }
  
    if (workingDays + leaveDays !== this.totalDays) {
      this.tostar.error(`Total days (${workingDays + leaveDays}) must be equal to total days (${this.totalDays}).`);
      return false;
    }
  
    return true;
  }
  
  onSave(index: number): void {
    if (!this.fromDate || !this.toDate) {
      this.tostar.error("Please select From Date and To Date.");
      return;
    }
  
    if (!this.validateDays(index)) {
      return;
    }
  
    const employee = this.employeeData[index];
    const workingDays = this.workingDaysMap[index] || 0;
    const leaveDays = this.leaveDaysMap[index] || 0;
  
    const jsonData = {
      employeeCode: employee.newJoinId,
      wagesFromDate: this.fromDate,
      wagesToDate: this.toDate,
      daysWorked: workingDays,
      totalLeaves: leaveDays,
      basicPay: employee.salaryValueFixed,
      payPerDay: (employee.salaryValueFixed / (this.totalDays || 1)).toFixed(2),
      wagesMonth: this.selectedMonth,
      adminStatus: 'NOT APPROVED',
      doctorStatus: 'APPROVED'
    };
  
    this.saveEmployeePayroll(jsonData, index);
  }
  
  saveEmployeePayroll(jsonData: any, index: number) {
    this.spinner.show();
    this.appService.saveEmployeePayroll(jsonData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.spinner.hide();
        this.savedRecords.add(index);
        this.tostar.success("Attendance details saved successfully.");
      }, (error: any) => {
        this.spinner.hide();
        this.tostar.error(error.message || "Something Went Wrong, Please Try Again.");
      });
  }
}
